import { ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";

/**
 * Video embed for audition profiles. Recognises YouTube, Vimeo, and Google
 * Drive share URLs and renders an iframe pointed at each provider's embed
 * endpoint. Anything else falls back to a plain "Open in new tab" link.
 * A ``seekSec`` prop bumps the iframe with the provider's start= query so
 * timestamped notes can jump the video without a player-API dependency.
 */
function toEmbed(url, startSec = 0) {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      return id ? { provider: "youtube", src: `https://www.youtube.com/embed/${id}${startSec ? `?start=${startSec}` : ""}` } : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v")
        || (u.pathname.startsWith("/shorts/") ? u.pathname.split("/")[2] : null)
        || (u.pathname.startsWith("/embed/") ? u.pathname.split("/")[2] : null);
      return id ? { provider: "youtube", src: `https://www.youtube.com/embed/${id}${startSec ? `?start=${startSec}` : ""}` } : null;
    }
    if (host === "vimeo.com" || host === "player.vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      return id && /^\d+$/.test(id)
        ? { provider: "vimeo", src: `https://player.vimeo.com/video/${id}${startSec ? `#t=${startSec}s` : ""}` }
        : null;
    }
    if (host === "drive.google.com") {
      const m = u.pathname.match(/\/file\/d\/([^/]+)/);
      const id = m ? m[1] : u.searchParams.get("id");
      return id ? { provider: "drive", src: `https://drive.google.com/file/d/${id}/preview` } : null;
    }
  } catch (_) { /* not a valid URL — fall through */ }
  return null;
}

export default function VideoEmbed({ url, label, seekSec }) {
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => { setReloadKey((k) => k + 1); }, [seekSec]);

  if (!url) return <div className="text-zinc-600 italic text-xs">— not submitted —</div>;
  const embed = toEmbed(url, seekSec || 0);
  if (!embed) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 text-gold underline hover:no-underline text-sm break-all"
         data-testid={`video-link-${label?.toLowerCase() || "raw"}`}>
        {url} <ExternalLink className="w-3 h-3 shrink-0" />
      </a>
    );
  }
  return (
    <div className="w-full">
      <div className="relative w-full rounded-md overflow-hidden border border-white/10 bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe
          key={reloadKey}
          src={embed.src}
          title={`${label || "Audition"} video`}
          data-testid={`video-embed-${label?.toLowerCase() || "raw"}`}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer"
         className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-gold">
        Open on {embed.provider} <ExternalLink className="w-2.5 h-2.5" />
      </a>
    </div>
  );
}
